import React, { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Zap, Play, Volume2, VolumeX } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { ASSETS } from "../../lib/data";
import { MaskLine } from "../motion/Reveal";
import { api } from "../../lib/api";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Hero = () => {
  const { t } = useLang();
  const [heroFilm, setHeroFilm] = useState(null);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/hero-film").then((r) => { if (r.data.url) setHeroFilm(r.data); }).catch(() => {});
  }, []);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    v.play().catch(() => {});
  };

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="hero" ref={ref} className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        {heroFilm ? (
          <video
            key={heroFilm.url}
            ref={videoRef}
            src={`${BACKEND_URL}${heroFilm.url}`}
            poster={heroFilm.poster_url ? `${BACKEND_URL}${heroFilm.poster_url}` : ASSETS.hero}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          />
        ) : (
          <img src={ASSETS.hero} alt="African eco-village at dusk" className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-charcoal" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity }}
        className="relative z-10 h-full max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-center"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="text-lime2 text-xs md:text-sm uppercase tracking-[0.35em] mb-6 font-medium"
        >
          {t.hero.kicker}
        </motion.p>

        <h1 className="font-display font-extrabold leading-[0.85] tracking-tighter text-offwhite text-[19vw] sm:text-[15vw] md:text-[11vw] lg:text-[9.5rem]">
          <MaskLine delay={0.35}>{t.hero.line1}</MaskLine>
          <MaskLine delay={0.5} className="text-emerald2">{t.hero.line2}</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-white/75 text-base md:text-lg leading-relaxed"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollTo("contact")}
            data-testid="hero-quote-btn"
            className="group inline-flex items-center gap-2 bg-emerald2 hover:bg-lime2 hover:text-black text-white font-semibold rounded-full px-7 py-4 transition-colors duration-300"
          >
            <Zap size={18} /> {t.hero.cta}
          </button>
          <button
            onClick={() => scrollTo("story")}
            data-testid="hero-watch-btn"
            className="group inline-flex items-center gap-2 text-white/90 hover:text-lime2 font-medium transition-colors duration-300"
          >
            <span className="grid place-items-center w-11 h-11 rounded-full border border-white/30 group-hover:border-lime2 transition-colors">
              <Play size={15} className="ml-0.5" />
            </span>
            {t.hero.cta2}
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/50"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>

      {heroFilm && (
        <motion.div
          style={{ opacity }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="absolute bottom-8 right-6 md:right-10 z-10 hidden sm:flex items-center gap-2.5"
        >
          <button
            onClick={toggleMute}
            data-testid="hero-mute-toggle"
            aria-label={muted ? "Unmute film" : "Mute film"}
            className="grid place-items-center w-10 h-10 rounded-full border border-white/15 bg-black/30 backdrop-blur-md text-white/80 hover:text-lime2 hover:border-lime2/60 transition-colors duration-300"
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <button
            onClick={() => scrollTo("story")}
            data-testid="hero-nowplaying"
            className="group inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-black/30 backdrop-blur-md px-4 py-2.5 hover:border-lime2/60 transition-colors duration-300"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-lime2 opacity-70 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-lime2" />
            </span>
            <span className="text-xs tracking-wide text-white/80 group-hover:text-lime2 transition-colors">
              {t.hero.nowPlaying}
            </span>
            <Play size={12} className="text-white/60 group-hover:text-lime2 transition-colors" />
          </button>
        </motion.div>
      )}
    </section>
  );
};
